import { FieldProps, getIn, FormikProps } from 'formik'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useIntl } from 'react-intl'
import { Error, FieldContainer, FormikField as Field, Label } from '../../style'
import { messages } from './messages'

interface Props extends FieldProps {
  id: string
  label?: string
  name: string
  disabled?: boolean
  placeholder?: string
  required?: boolean
  onValueChange?: (_: any, form: FormikProps<any>) => void
  onBlur?: (form: FormikProps<any>) => void
  type?: string
  toUpperCase?: boolean
  maxLength?: number
  autoComplete?: "on" | "off"
  trim?: boolean
}

interface CustomCityInputProps extends Props {
  hasCoordinates?: boolean
}

export const TextField: React.FunctionComponent<Props & FieldProps> = ({
  id,
  field,
  form,
  label,
  placeholder,
  disabled = false,
  required,
  onValueChange,
  onBlur,
  trim = false,
  type = 'text',
  toUpperCase = false,
  autoComplete = "off",
  maxLength = 1000,
  ...rest
}) => {
  const { formatMessage } = useIntl()
  const [value, setValue] = useState<string | ''>(field.value || '')
  const [truncated, setTruncated] = useState(false)
  const pasteExceededRef = useRef(false)

  useEffect(() => {
    const newValue = field.value || ''
    setValue(newValue)
  }, [field.value])

  const handleChange = useCallback(
    (event: any) => {
      const targetValue = event.target.value as string

      let trimmedValue = targetValue.trim()

      if (toUpperCase) trimmedValue = trimmedValue.toUpperCase()

      setValue(trim ? trimmedValue : targetValue)
      form.setFieldTouched(field.name, true, false)
      form.setFieldValue(field.name, trimmedValue, true)

      if (pasteExceededRef.current) {
        pasteExceededRef.current = false
        setTruncated(true)
      } else {
        setTruncated(false)
      }

      if (onValueChange) {
        onValueChange(event, form)
      }
    },
    [field, form, onValueChange, toUpperCase, trim]
  )

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = event.clipboardData.getData('text/plain')
      const currentLength = event.currentTarget.value.length
      pasteExceededRef.current = currentLength + pastedText.length > maxLength
    },
    [maxLength]
  )

  const handleBlur = useCallback(() => {
    setTruncated(false)
    if (field.value && onBlur) {
      onBlur(form)
    }
  }, [field.value, onBlur, form])

  const fieldError = getIn(form.errors, field.name)
  const fieldTouched = getIn(form.touched, field.name)
  const hasErrors = Boolean(fieldError) && Boolean(fieldTouched)
  const charCount = (value || '').length
  const showCounter = truncated || charCount > maxLength * 0.8

  return (
    <FieldContainer {...rest}>
      {label && <Label required={required}>{label}</Label>}
      <StyledField
        id={id}
        type={type}
        name={field.name}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        onPaste={handlePaste}
        autoComplete={autoComplete}
        maxLength={maxLength}
        //hasCoordinates={isCityInputAndHasCoordinates}
      />
      {truncated && (
        <PasteTruncatedWarning>
          {formatMessage(messages.pasteTruncatedWarning, { maxLength })}
        </PasteTruncatedWarning>
      )}
      {!truncated && showCounter && <CharCount at_limit={charCount >= maxLength}>{charCount} / {maxLength}</CharCount>}
      {hasErrors && <Error className="error-tooltip">{fieldError}</Error>}
    </FieldContainer>
  )
}

// const checkForCoordinates = (formik: FieldProps) => {
//   const { field, form } = formik
//   if (!field.name.includes('city')) return false
//   const coords = getIn(form.values, field.name.replace('city', 'coords'))
//   if (coords && coords.lat && coords.long) return true
//   return false
// }

const StyledField = styled(Field as any)<CustomCityInputProps>`
  background-color: ${props => props.theme.colors.white};
  width: 100%;
  height: ${props => props.theme.inputFieldHeight};
  color: ${props => (props.color ? props.theme.colors[props.color] : props.theme.colors.black)};
  ${props =>
    props.disabled &&
    css`
      color: ${props => props.theme.colors.greyDisabled} !important;
    `};
  background-color: ${props =>
    props.disabled
      ? props.theme.colors.grey200
      : props.hasCoordinates
      ? props.theme.colors.green100
      : props.theme.colors.white};
  ${props =>
    css`
      border: 1px solid ${props.theme.colors.grey300};
    `}
`

const CharCount = styled.span<{ at_limit: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  font-size: 0.7rem;
  color: ${props => props.at_limit ? props.theme.colors.red100 : props.theme.colors.grey500};
  pointer-events: none;
`

const PasteTruncatedWarning = styled.span`
  position: absolute;
  bottom: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  background-color: ${props => props.theme.colors.yellow100};
  color: ${props => props.theme.colors.grey700};
  border-left: 3px solid ${props => props.theme.colors.yellow700};
  border-radius: ${props => props.theme.radius.medium};
  pointer-events: none;
`
// 2px solid #43a047
